import React, { useState } from "react";

const load = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

export default function Resident360({ user }: { user?: any }) {
  const residents = load("residents_records");
  const [name, setName] = useState("");
  const r = residents.find((x: any) => x.name === name);

  const invoices = load("invoices_records").filter((i: any) => i.residentName === name);
  const intelligence = load("intelligence_reports").filter((i: any) => i.residentName === name || i.resident === name);
  const wellbeing = load("wellbeing_records").filter((w: any) => w.residentName === name);
  const wounds = load("rmacs_wound_vault").filter((w: any) => w.resident_name === name);
  const eol = load("eol_records").filter((e: any) => e.residentName === name);
  const mca = load("mca_hub_records").filter((m: any) => m.residentName === name);
  const meds = load("meds_register").filter((m: any) => m.residentName === name);
  const mar = load("mar_entries").filter((m: any) => m.residentName === name && m.status !== "Not required");
  const safeg = load("rmacs_safeguarding_vault").filter((s: any) => s.resident_name === name);

  const checks = [
    { label: "Funding type & council/private shares recorded", ok: !!(r && ((r.fundingType || r.funder) && (parseFloat(r.residentPays) > 0 || parseFloat(r.councilPays) > 0 || r.funder === "Private" || r.fundingType === "Self-funding"))) },
    { label: "Next of kin / legal representative recorded", ok: !!(r && (r.familyName || r.legalRep)) },
    { label: "Admission intelligence / assessment on file", ok: intelligence.length > 0 },
    { label: "Medication registered", ok: meds.length > 0 },
    { label: "Wellbeing review in the last 60 days", ok: wellbeing.some((w: any) => new Date(`${w.month}-01`).getTime() > Date.now() - 60 * 86400000) },
  ];
  const passed = checks.filter(c => c.ok).length;
  const score = checks.length ? Math.round((passed / checks.length) * 100) : 0;

  const marGiven = mar.filter((m: any) => m.status === "Given").length;
  const marPct = mar.length ? Math.round((marGiven / mar.length) * 100) : null;
  const invoicedTotal = invoices.reduce((s: number, i: any) => s + (parseFloat(i.customerTotal) || parseFloat(i.total) || 0), 0);

  const printSummary = () => {
    if (!r) return;
    const sections = [
      ["Resident", `${r.name} | Room ${r.room || "-"} | Admitted ${r.admissionDate || "-"}${r.exitDate ? ` | Left ${r.exitDate}` : ""}`],
      ["Funding", `${r.fundingType || r.funder || "-"}${r.council ? ` (${r.council})` : ""} | Customer £${r.residentPays || 0} | Council/NHS £${r.councilPays || 0}`],
      ["Next of kin", `${r.familyName || "-"}${r.relation ? ` (${r.relation})` : ""} | Legal rep: ${r.legalRep || "-"}`],
      ["Assessments", `${intelligence.length} intelligence/assessment report(s)`],
      ["Invoices", `${invoices.length} invoice(s), total £${invoicedTotal.toFixed(2)}`],
      ["Wellbeing", `${wellbeing.length} review(s)`],
      ["Wounds", `${wounds.length} wound chart(s)`],
      ["Medication", `${meds.length} medication(s) | MAR compliance ${marPct !== null ? marPct + "%" : "no data"}`],
      ["MCA / DoLS", `${mca.length} record(s)`],
      ["End of life", `${eol.length} plan(s)`],
      ["Safeguarding", `${safeg.length} record(s)`],
      ["Completeness", `${score}% - ${checks.filter(c => !c.ok).map(c => c.label).join("; ") || "complete"}`],
    ].map(row => `<tr><td style="padding:6px;border-bottom:1px solid #eee;font-weight:bold;width:30%">${row[0]}</td><td style="padding:6px;border-bottom:1px solid #eee">${row[1]}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Resident 360</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:14px}</style></head><body><h2>Resident 360 Summary - Roop Cottage</h2><p>Generated ${new Date().toLocaleString()}</p><table>${sections}</table></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Resident 360 Record Hub</h2>
        <p className="text-sm text-slate-600">Everything about one resident on a single screen - funding, assessments, invoices, wellbeing, clinical and rights records - with a completeness score that exposes any gap.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <select value={name} onChange={e => setName(e.target.value)} className="w-full max-w-md rounded-lg border border-slate-300 px-2 py-1 text-sm">
          <option value="">Choose resident</option>
          {residents.map((res: any) => <option key={res.id} value={res.name}>{res.name}</option>)}
        </select>
      </div>

      {r ? (
        <>
          <div className={`rounded-xl border-2 p-4 shadow-sm ${score === 100 ? "border-green-300 bg-green-50" : score >= 60 ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-lg font-bold text-slate-900">{r.name} <span className="text-sm font-normal text-slate-600">Room {r.room || "-"} | Admitted {r.admissionDate || "-"}</span></div>
              <div className="text-2xl font-bold text-slate-900">{score}%</div>
            </div>
            <div className="mt-2 space-y-1">
              {checks.map(c => (
                <div key={c.label} className="flex items-center gap-2 text-sm">
                  <span className={c.ok ? "text-green-700" : "text-red-700"}>{c.ok ? "✓" : "✗"}</span>
                  <span className={c.ok ? "text-slate-700" : "font-semibold text-red-700"}>{c.label}</span>
                </div>
              ))}
            </div>
            {score < 100 ? <div className="mt-2 text-xs text-red-700">Close the red gaps before inspection - each one maps to a module in the sidebar.</div> : <div className="mt-2 text-xs text-green-700">Record complete - inspection ready.</div>}
          </div>

          <div className="flex gap-2">
            <button onClick={printSummary} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Print 360 summary</button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Funding & billing</h3>
              <div className="text-sm text-slate-700">{r.fundingType || r.funder || "-"}{r.council ? ` (${r.council})` : ""} | Customer £{r.residentPays || 0} | Council/NHS £{r.councilPays || 0}</div>
              <div className="mt-1 text-sm text-slate-700">{invoices.length} invoice(s) | total £{invoicedTotal.toFixed(2)}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Family & rights</h3>
              <div className="text-sm text-slate-700">Next of kin: {r.familyName || "-"}{r.relation ? ` (${r.relation})` : ""}</div>
              <div className="text-sm text-slate-700">Legal representative: {r.legalRep || "-"}</div>
              <div className="text-sm text-slate-700">MCA/DoLS records: {mca.length}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Clinical</h3>
              <div className="text-sm text-slate-700">Assessments: {intelligence.length} | Wound charts: {wounds.length}</div>
              <div className="text-sm text-slate-700">Medications: {meds.length} | MAR compliance: {marPct !== null ? `${marPct}%` : "no data"}</div>
              <div className="text-sm text-slate-700">End-of-life plan: {eol.length ? "in place" : "none recorded"}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-slate-900">Wellbeing & safeguarding</h3>
              <div className="text-sm text-slate-700">Wellbeing reviews: {wellbeing.length}</div>
              <div className="text-sm text-slate-700">Safeguarding records: {safeg.length}</div>
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Choose a resident to see their complete 360 record.</div>
      )}
    </div>
  );
}